import { useEffect, useMemo } from 'react'

import { useRouter } from 'next/router'

import { useAuthInfo } from '@sweetauth/core'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { usePathname } from '@/hooks/common/usePathname'

export const useIsTrialPeriod = () => {
  const { user, loading: userIsLoading } = useAuthInfo()
  const { data: billingOrganization, isLoading: billingOrgIsLoading } =
    useBillingOrganization()

  const pathname = usePathname()

  // using router.push() instead of safePush() because safePush() depends useIsTrialPeriod()
  // because of the weird nextjs navigation timeout bug that's happening, see useSafePush for more details
  const router = useRouter()

  const isLoading = useMemo(() => {
    return (userIsLoading || billingOrgIsLoading) && user !== null
  }, [userIsLoading, billingOrgIsLoading, user])

  const timeRemaining = useMemo(() => {
    if (
      billingOrganization === undefined ||
      billingOrganization === null ||
      billingOrganization.tier !== 'free'
    ) {
      return Infinity
    }
    // calculate time remaining in seconds since epoch
    const now = new Date()
    const nowUTC = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
    )
    return Math.max(
      (billingOrganization.trial_ends_at.getTime() - nowUTC) / 1000,
      0,
    )
  }, [billingOrganization])

  const isTrialPeriod = useMemo(() => {
    if (
      billingOrganization === undefined ||
      billingOrganization === null ||
      billingOrganization.tier !== 'free'
    ) {
      return false
    }
    return timeRemaining > 0
  }, [billingOrganization, user, timeRemaining])

  const trialExpired = useMemo(() => {
    if (
      billingOrganization === undefined ||
      billingOrganization === null ||
      billingOrganization.tier !== 'free'
    ) {
      return false
    }
    return timeRemaining <= 0
  }, [billingOrganization, timeRemaining])

  const redirect = useMemo(() => {
    // DO NOT REMOVE THE PATHNAME CHECK;
    // IT PREVENTS AN INFINITE REDIRECT TO THE UPGRADE PAGE
    return (
      timeRemaining === 0 &&
      !(pathname ?? '').includes('/upgrade') &&
      !(pathname ?? '').includes('/settings')
    )
  }, [timeRemaining, pathname])

  useEffect(() => {
    if (redirect) {
      router.push('/upgrade')
    }
  }, [redirect])

  return {
    isLoading,
    isTrialPeriod,
    daysRemaining: Math.ceil(timeRemaining / 60 / 60 / 24),
    redirect,
    trialExpired,
    tier: billingOrganization?.tier, // for convenience
  }
}
